import { View, Text, styled } from 'tamagui'
import { Shield, Mic, MicOff, Headphones, VolumeX, Camera, CameraOff, Monitor } from '@tamagui/lucide-icons'
import { useAppStore, useServerStore } from '@/store'
import { voiceChannelParticipants, users } from '@/mocks'
import { Avatar } from '../../common'
import type { VoiceParticipant } from '@/types'

const Container = styled(View, {
  flex: 1,
  flexDirection: 'column',
  backgroundColor: '$surface',
})

const VoiceHeader = styled(View, {
  height: 48,
  paddingHorizontal: 16,
  flexDirection: 'row',
  alignItems: 'center',
  borderBottomWidth: 1,
  borderBottomColor: '$borderDefault',
  gap: 10,
  alignSelf: 'stretch',
})

const ChannelName = styled(Text, {
  fontFamily: '$fontHeading',
  fontWeight: '600',
  fontSize: 15,
  color: '$surfaceForeground',
  flex: 1,
})

const E2eBadge = styled(View, {
  flexDirection: 'row',
  alignItems: 'center',
  gap: 4,
  backgroundColor: '$accentLighter',
  borderRadius: '$borderRadiusSm',
  paddingHorizontal: 8,
  paddingVertical: 3,
  borderWidth: 1,
  borderColor: '$accent',
})

const ParticipantGrid = styled(View, {
  flexDirection: 'row',
  flexWrap: 'wrap',
  justifyContent: 'center',
  gap: 24,
  padding: 48,
  maxWidth: 800,
})

const ParticipantCard = styled(View, {
  width: 140,
  alignItems: 'center',
  gap: 8,
  padding: 16,
  borderRadius: '$borderRadiusXl',
  backgroundColor: '$surfaceRaised',
  borderWidth: 1,
  borderColor: '$borderDefault',
})

const SpeakingRing = styled(View, {
  borderRadius: '$borderRadiusFull',
  padding: 3,
})

const StatusIcons = styled(View, {
  flexDirection: 'row',
  gap: 6,
  alignItems: 'center',
})

const EmptyState = styled(View, {
  alignItems: 'center',
  gap: 12,
  padding: 48,
})

const SpatialHint = styled(View, {
  flexDirection: 'row',
  alignItems: 'center',
  gap: 8,
  paddingHorizontal: 16,
  paddingVertical: 8,
  borderRadius: '$borderRadiusLg',
  backgroundColor: '$surfaceRaised',
  borderWidth: 1,
  borderColor: '$borderDefault',
  marginBottom: 16,
})

// Video tile: dark rectangle with initials for camera-on participants
const VideoTile = styled(View, {
  width: 64,
  height: 64,
  borderRadius: '$borderRadiusLg',
  backgroundColor: '$sidebar',
  alignItems: 'center',
  justifyContent: 'center',
  borderWidth: 1,
  borderColor: '$borderDefault',
})

const VideoInitials = styled(Text, {
  fontFamily: '$fontHeading',
  fontWeight: '700',
  fontSize: 22,
  color: '$surfaceForeground',
})

// Camera toggle button in participant card
const CameraButton = styled(View, {
  width: 24,
  height: 24,
  borderRadius: '$borderRadiusSm',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  hoverStyle: {
    backgroundColor: '$sidebarHover',
  },
  // @ts-expect-error -- web transition
  style: { transition: 'background-color 150ms ease' },
})

// Screen share stage
const StageContainer = styled(View, {
  flex: 1,
  flexDirection: 'column',
})

const StageArea = styled(View, {
  flex: 7,
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: '$sidebar',
  borderBottomWidth: 1,
  borderBottomColor: '$borderDefault',
  padding: 32,
})

const StageScreen = styled(View, {
  width: '80%',
  maxWidth: 640,
  aspectRatio: 16 / 9,
  backgroundColor: '$surface',
  borderRadius: '$borderRadiusLg',
  borderWidth: 1,
  borderColor: '$borderDefault',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 12,
})

const StageLabel = styled(Text, {
  fontFamily: '$fontBody',
  fontSize: 14,
  color: '$sidebarMuted',
})

const ParticipantStrip = styled(View, {
  flex: 3,
  minHeight: 120,
  maxHeight: 200,
})

const ParticipantStripScroll = styled(View, {
  flexDirection: 'row',
  alignItems: 'center',
  gap: 16,
  paddingHorizontal: 24,
  paddingVertical: 16,
  // @ts-expect-error -- web overflow
  style: { overflowX: 'auto', overflowY: 'hidden' },
})

const SharingBadge = styled(View, {
  flexDirection: 'row',
  alignItems: 'center',
  gap: 3,
  backgroundColor: '$accentLighter',
  borderRadius: '$borderRadiusSm',
  paddingHorizontal: 6,
  paddingVertical: 2,
  borderWidth: 1,
  borderColor: '$accent',
})

interface VoiceChannelViewProps {
  channelId: string
}

function getInitials(name: string): string {
  const parts = name.split(' ')
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase()
  return name.slice(0, 2).toUpperCase()
}

function ParticipantCardView({ p, showCameraToggle = true }: { p: VoiceParticipant; showCameraToggle?: boolean }) {
  const user = users[p.userId]
  if (!user) return null

  return (
    <ParticipantCard
      key={p.userId}
      {...(p.isSpeaking && {
        borderColor: '$warning',
        // @ts-expect-error -- web box-shadow
        style: {
          boxShadow: '0 0 16px rgba(232, 168, 64, 0.2)',
          animation: 'voicePulse 1.5s ease-in-out infinite',
        },
      })}
    >
      {/* Show video tile when camera is on, otherwise show avatar */}
      {p.isCameraOn ? (
        <VideoTile>
          <VideoInitials>{getInitials(user.displayName)}</VideoInitials>
        </VideoTile>
      ) : (
        <SpeakingRing
          borderWidth={p.isSpeaking ? 2 : 0}
          borderColor={p.isSpeaking ? '$warning' : 'transparent'}
        >
          <Avatar
            name={user.displayName}
            color={user.roleColor || '#8B95A8'}
            size="xl"
            showStatus={false}
            isSpeaking={p.isSpeaking}
          />
        </SpeakingRing>
      )}
      <Text fontFamily="$fontBody" fontSize={14} fontWeight="500" color="$surfaceForeground">
        {user.displayName}
      </Text>

      {/* Sharing badge */}
      {p.isScreenSharing && (
        <SharingBadge>
          <Monitor size={10} color="$accent" />
          <Text fontFamily="$fontMono" fontSize={10} fontWeight="600" color="$accent">Sharing</Text>
        </SharingBadge>
      )}

      <StatusIcons>
        {p.isMuted ? (
          <MicOff size={14} color="$error" />
        ) : (
          <Mic size={14} color={p.isSpeaking ? '$warning' : '$sidebarMuted'} />
        )}
        {p.isDeafened && <VolumeX size={14} color="$error" />}
        {showCameraToggle && (
          <CameraButton>
            {p.isCameraOn ? (
              <Camera size={14} color="$accent" />
            ) : (
              <CameraOff size={14} color="$sidebarMuted" />
            )}
          </CameraButton>
        )}
        <Shield size={12} color="$accent" />
      </StatusIcons>
    </ParticipantCard>
  )
}

export function VoiceChannelView({ channelId }: VoiceChannelViewProps) {
  const servers = useServerStore((s) => s.servers)

  let channelName = 'Voice'
  let serverName = ''
  for (const server of Object.values(servers)) {
    const ch = server.channels.find(c => c.id === channelId)
    if (ch) {
      channelName = ch.name
      serverName = server.name
      break
    }
  }

  const participants = voiceChannelParticipants[channelId] || []
  const screenSharer = participants.find(p => p.isScreenSharing)
  const screenShareUser = screenSharer ? users[screenSharer.userId] : null

  return (
    <Container>
      <VoiceHeader>
        <Text fontSize={16} color="$warning">&#x1F50A;</Text>
        <ChannelName>{channelName}</ChannelName>
        <E2eBadge>
          <Shield size={12} color="$accent" />
          <Text fontFamily="$fontMono" fontSize={11} fontWeight="600" color="$accent">E2E</Text>
        </E2eBadge>
        <Text fontFamily="$fontBody" fontSize={12} color="$sidebarMuted">{serverName}</Text>
      </VoiceHeader>

      {participants.length === 0 ? (
        <View flex={1} alignItems="center" justifyContent="center">
          <EmptyState>
            <Text fontSize={48} opacity={0.3}>&#x1F50A;</Text>
            <Text fontFamily="$fontBody" fontSize={16} color="$sidebarMuted">
              No one else is here yet
            </Text>
            <Text fontFamily="$fontBody" fontSize={13} color="$color8">
              Invite friends to join this voice channel
            </Text>
          </EmptyState>
        </View>
      ) : screenSharer && screenShareUser ? (
        /* Screen share stage layout */
        <StageContainer>
          <StageArea>
            <StageScreen>
              <Monitor size={48} color="$sidebarMuted" />
              <StageLabel>{screenShareUser.displayName} is sharing their screen</StageLabel>
            </StageScreen>
          </StageArea>

          <ParticipantStrip>
            <View alignItems="center" paddingTop={8}>
              <SpatialHint>
                <Text fontSize={14}>&#x1F3A7;</Text>
                <Text fontFamily="$fontBody" fontSize={12} color="$sidebarMuted">
                  Spatial audio active — participants are positioned in 3D space
                </Text>
              </SpatialHint>
            </View>
            <ParticipantStripScroll>
              {participants.map((p) => (
                <ParticipantCardView key={p.userId} p={p} />
              ))}
            </ParticipantStripScroll>
          </ParticipantStrip>
        </StageContainer>
      ) : (
        /* Normal grid layout */
        <View flex={1} alignItems="center" justifyContent="center">
          <View alignItems="center">
            <SpatialHint>
              <Text fontSize={14}>&#x1F3A7;</Text>
              <Text fontFamily="$fontBody" fontSize={12} color="$sidebarMuted">
                Spatial audio active — participants are positioned in 3D space
              </Text>
            </SpatialHint>

            <ParticipantGrid>
              {participants.map((p) => (
                <ParticipantCardView key={p.userId} p={p} />
              ))}
            </ParticipantGrid>
          </View>
        </View>
      )}
    </Container>
  )
}
